import dayjs from 'dayjs';

/**
 * "1. 9. 2026." - how a date is written in Serbian, on every Finance screen whatever the
 * interface language, and never as ISO. Takes YYYY-MM-DD or a timestamp; '—' for nothing.
 */
export const formatSerbianDate = (value?: string | null): string => {
  if (!value) return '—';
  const [year, month, day] = value.substring(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '—';
  return `${day}. ${month}. ${year}.`;
};

/** Whole days from `from` to `to`, both YYYY-MM-DD; negative when `to` is earlier. */
export const daysBetween = (from: string, to: string): number =>
  Math.round((Date.UTC(+to.slice(0, 4), +to.slice(5, 7) - 1, +to.slice(8, 10)) - Date.UTC(+from.slice(0, 4), +from.slice(5, 7) - 1, +from.slice(8, 10))) / 86_400_000);

export const formatDateWithDots = (date: string) => {
  const [year, month, day] = date.substring(0, 10).split('-');
  return `${day}.${month}.${year}.`;
};

/**
 * Formats a Date as YYYY-MM-DD using its LOCAL components.
 *
 * `toISOString()` converts to UTC first, so local midnight in Belgrade (UTC+2)
 * becomes 22:00 the previous day and the date silently shifts back by one.
 */
export const toLocalDateString = (date: Date): string =>
  [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');

/** First day of the previous month - the usual billing period start. */
export const firstDayOfPreviousMonth = (): string => {
  const now = new Date();
  return toLocalDateString(new Date(now.getFullYear(), now.getMonth() - 1, 1));
};

/** Last day of the previous month. */
export const lastDayOfPreviousMonth = (): string => {
  const now = new Date();
  return toLocalDateString(new Date(now.getFullYear(), now.getMonth(), 0));
};

/**
 * A month as `YYYY-MM`.
 *
 * One string rather than a year/month pair because it travels in the URL: it sorts
 * lexicographically, cannot get into a half-updated state, and carries no timezone. The
 * API takes the two numbers apart, so `parseMonthKey` splits it at the call site.
 *
 * Everything below is built from local Date components, never `toISOString()` - the same
 * trap `toLocalDateString` documents above.
 */
export const MONTH_KEY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The month an admin is looking at by default: the one being lived in. */
export const currentMonthKey = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/** Null for anything that is not a month key, so a junk URL falls back instead of 500ing. */
export const parseMonthKey = (value?: string | null): { year: number; month: number } | null => {
  if (!value || !MONTH_KEY_PATTERN.test(value)) return null;
  const [year, month] = value.split('-');
  return { year: Number(year), month: Number(month) };
};

/** `delta` months forward or back. */
export const shiftMonthKey = (value: string, delta: number): string => {
  const parsed = parseMonthKey(value) ?? parseMonthKey(currentMonthKey())!;
  const shifted = new Date(parsed.year, parsed.month - 1 + delta, 1);
  return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * `Septembar 2026` / `September 2026`, in whatever language the panel is in.
 *
 * Built through dayjs because the date pickers use the same dayjs locale for their month
 * grid: a label beside a calendar that names the month in another language reads as a bug.
 * The i18n setup switches the dayjs locale together with the interface language.
 */
export const formatMonthKey = (value: string): string => {
  if (!parseMonthKey(value)) return value;
  const label = dayjs(`${value}-01`).format('MMMM YYYY');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/** `Sep 26`, for a chart axis where the full name does not fit. */
export const formatMonthKeyShort = (value: string): string => {
  if (!parseMonthKey(value)) return value;
  return dayjs(`${value}-01`).format('MMM YY').replace('.', '');
};
